import React, { useEffect, useState } from "react";
import AddServiceModal from "../../components/Components/AddServiceModal";
import ServicesList from "../../components/Components/ServicesList";
import AdminApi from "../../components/AdminApi/Api/api";
import CenterRegistrationApi from "../../components/centerRegistration/Api/api";
import TicketAcceptanceManager from "./TicketAcceptanceManager";
import {
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Box,
  Typography,
  Button,
  Modal,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Chip,
  Avatar,
  Tooltip,
  useMediaQuery,
  useTheme,
  styled,
  Tabs,
  Tab,
  Fade,
  Slide,
  Zoom,
  Grow,
} from "@mui/material";
import { useSelector } from "react-redux";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Category as CategoryIcon,
  Dashboard as DashboardIcon,
} from "@mui/icons-material";
import {
  canViewServices,
  canAddPrimaryService,
  canUpdatePrimaryService,
  canDeletePrimaryService,
  canViewClientServices,
  canAddClientService,
  canUpdateClientService,
  canDeleteClientService,
  canViewTicketAcceptance,
} from "../../utility/permissionUtils";

// Styled components with Tailwind-like utility classes
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
    transform: "translateY(-2px)",
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: "12px",
  overflow: "hidden",
  "& .MuiTable-root": {
    minWidth: 650,
  },
  "& .MuiTableCell-root": {
    padding: theme.spacing(1.5),
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
  },
  "& .MuiTableHead-root": {
    backgroundColor: "#f8fafc",
  },
  "& .MuiTableRow-root:hover": {
    backgroundColor: "#f8fafc",
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 500,
  borderRadius: "8px",
  padding: "10px 20px",
  boxShadow: "none",
  transition: "all 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
    transform: "translateY(-1px)",
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: "1px solid #e2e8f0",
  marginBottom: theme.spacing(3),
  "& .MuiTab-root": {
    textTransform: "none",
    fontWeight: 500,
    minWidth: 120,
    borderRadius: "8px 8px 0 0",
    marginRight: "8px",
    "&.Mui-selected": {
      color: theme.palette.primary.main,
    },
  },
  "& .MuiTabs-indicator": {
    height: "3px",
    borderRadius: "3px 3px 0 0",
  },
}));

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: "85%", md: "700px" },
  bgcolor: "background.paper",
  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
  borderRadius: "16px",
  p: { xs: 3, sm: 4 },
  maxHeight: "90vh",
  overflowY: "auto",
  outline: "none",
};

const AnimatedCard = styled(Card)(({ theme }) => ({
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 12px 30px rgba(0, 0, 0, 0.15)",
  },
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`services-tabpanel-${index}`}
      aria-labelledby={`services-tab-${index}`}
      {...other}
    >
      <Fade in={value === index} timeout={300}>
        <Box sx={{ py: 3 }}>{children}</Box>
      </Fade>
    </div>
  );
}

export default function AdminDashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [servicesList, setServicesList] = useState([]);
  const [primaryServices, setPrimaryServices] = useState([]);
  const [clientServices, setClientServices] = useState([]);
  const [loadingClientServices, setLoadingClientServices] = useState(false);
  const [addClientServiceModalOpen, setAddClientServiceModalOpen] =
    useState(false);
  const [editClientServiceModalOpen, setEditClientServiceModalOpen] =
    useState(false);
  const [currentEditingService, setCurrentEditingService] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  // Get user permissions from Redux store
  const globalState = useSelector((state) => state.AdminReducer);
  const userPermissions = globalState?.adminDetails?.role_id?.permissions;
  const userRole = globalState?.adminDetails?.admin_role;

  // Snackbar state
  const [snackBarValues, setSnackBarValues] = useState({
    open: false,
    msg: "",
    duration: 5000,
    type: "",
  });

  // Client Service Modal state
  const [clientServiceForm, setClientServiceForm] = useState({
    selectedPrimaryService: "",
    category: "",
    subCategory: "",
    price: "",
    serviceItems: [],
  });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const showDialog = (open, type, msg) => {
    setSnackBarValues({
      open: open,
      msg: msg,
      duration: 5000,
      type: type,
    });
  };

  const closeDialog = (open = false) => {
    setSnackBarValues({
      open: open,
      msg: "",
      duration: 5000,
      type: "",
    });
  };

  // Get all services (including inactive ones)
  const getAllServicesExists = () => {
    if (!canViewServices) return;

    new CenterRegistrationApi()
      .getAllServices()
      .then((res) => {
        setServicesList(res.data);
      })
      .catch((err) => {
        console.log(err);
        showDialog(true, "error", "Failed to fetch services");
      });
  };

  // Get client services for all primary services
  const getAllClientServices = async () => {
    if (!canViewClientServices) return;

    setLoadingClientServices(true);
    try {
      // First get all primary services
      const primaryServicesRes =
        await new CenterRegistrationApi().getAllActiveServices();
      const primaryServicesData = primaryServicesRes.data;

      const allClientServices = [];

      for (const primaryService of primaryServicesData) {
        try {
          const res = await new AdminApi().getaddedServiceCategory(
            primaryService._id
          );
          if (
            res.data &&
            res.data.data &&
            res.data.data.names &&
            res.data.data.names.length > 0
          ) {
            const serviceNames = res.data.data.names;

            const transformedData = serviceNames.map((service) => ({
              id: service._id,
              category: service.label,
              subCategory: service.label2 || "",
              price: service.category_service_prize,
              primaryServiceId: primaryService._id,
              primaryServiceName: primaryService.service_name,
              status: "active",
            }));

            allClientServices.push(...transformedData);
          }
        } catch (err) {
          console.error(
            `Error fetching services for primary service ${primaryService._id}:`,
            err
          );
        }
      }

      setClientServices(allClientServices);
      setPrimaryServices(primaryServicesData);
    } catch (err) {
      console.error("Error:", err);
      showDialog(true, "error", "Failed to load client services");
    } finally {
      setLoadingClientServices(false);
    }
  };

  // Get only active primary services for the dropdown
  const getAllActivePrimaryServices = () => {
    if (!canViewServices) return;

    new CenterRegistrationApi()
      .getAllActiveServices()
      .then((res) => {
        setPrimaryServices(res.data);
      })
      .catch((err) => {
        console.log(err);
        showDialog(true, "error", "Failed to fetch active services");
      });
  };

  const onDeleteClientService = (serviceId) => {
    if (!canDeleteClientService) {
      showDialog(
        true,
        "error",
        "You don't have permission to delete client services"
      );
      return;
    }

    if (
      window.confirm(`Are you sure you want to delete this service category?`)
    ) {
      if (!serviceId) {
        showDialog(true, "error", "Invalid service ID");
        return;
      }

      new AdminApi()
        .deleteClientService(serviceId)
        .then((res) => {
          if (res && res.success) {
            showDialog(
              true,
              "success",
              res.message || "Service category deleted successfully"
            );
            setClientServices((prev) =>
              prev.filter((service) => service.id !== serviceId)
            );
          } else {
            showDialog(
              true,
              "error",
              res?.message || "Failed to delete service category"
            );
          }
        })
        .catch((err) => {
          console.error("Delete error:", err);
          let errorMessage = "Failed to delete service category";

          // Handle specific error cases
          if (err?.response?.data?.status === 410) {
            errorMessage = "Category not found or already deleted";
            // Remove from local state if the API says it's already gone
            setClientServices((prev) =>
              prev.filter((service) => service.id !== serviceId)
            );
          } else {
            errorMessage = err?.response?.data?.message || errorMessage;
          }

          showDialog(true, "error", errorMessage);
        });
    }
  };

  const onEditClientService = (service) => {
    if (!canUpdateClientService) {
      showDialog(
        true,
        "error",
        "You don't have permission to edit client services"
      );
      return;
    }

    setCurrentEditingService(service);
    setClientServiceForm({
      selectedPrimaryService: service.primaryServiceId,
      category: service.category,
      subCategory: service.subCategory,
      price: service.price,
      serviceItems: [
        {
          label: service.category,
          label2: service.subCategory,
          category_service_prize: service.price,
        },
      ],
    });
    setEditClientServiceModalOpen(true);
  };

  const handleUpdateClientService = () => {
    if (!canUpdateClientService) {
      showDialog(
        true,
        "error",
        "You don't have permission to update client services"
      );
      return;
    }

    const { selectedPrimaryService, serviceItems } = clientServiceForm;

    if (!selectedPrimaryService || serviceItems.length === 0) {
      showDialog(true, "error", "Please add at least one service item");
      return;
    }

    if (!currentEditingService?.id) {
      showDialog(true, "error", "Invalid service to update");
      return;
    }

    const payload = {
      category_id: currentEditingService.id,
      primary_services: selectedPrimaryService,
      names: serviceItems.map((item) => ({
        _id: item._id,
        label: item.label,
        label2: item.label2 || "",
        category_service_prize: parseFloat(item.category_service_prize) || 0,
      })),
    };

    new AdminApi()
      .updateClientService(payload)
      .then((res) => {
        if (res && res.success) {
          showDialog(true, "success", "Client service updated successfully");
          getAllClientServices();
          setEditClientServiceModalOpen(false);
          setCurrentEditingService(null);
          setClientServiceForm({
            selectedPrimaryService: "",
            category: "",
            subCategory: "",
            price: "",
            serviceItems: [],
          });
        } else {
          showDialog(true, "error", res?.message || "Failed to update service");
        }
      })
      .catch((err) => {
        console.error("Update error:", err);
        showDialog(
          true,
          "error",
          err?.response?.data?.message || "Failed to update service"
        );
      });
  };

  const onDeleteService = (serviceId) => {
    if (!canDeletePrimaryService) {
      showDialog(
        true,
        "error",
        "You don't have permission to delete primary services"
      );
      return;
    }

    let payload = {
      serviceId: serviceId,
    };
    new AdminApi()
      .deleteService(payload)
      .then((res) => {
        if (res) {
          showDialog(true, "success", "Service deleted successfully");
        }
        getAllServicesExists();
        getAllClientServices();
      })
      .catch((err) => {
        console.log(err);
        showDialog(true, "error", err?.response?.data?.message);
      });
  };

  useEffect(() => {
    if (canViewServices) {
      getAllServicesExists();
    }
    if (canViewClientServices) {
      getAllClientServices();
    }
  }, []);

  // Client Service Modal functions
  const handleClientServiceChange = (e) => {
    const { name, value } = e.target;
    setClientServiceForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddServiceItem = () => {
    const { category, subCategory, price } = clientServiceForm;
    if (!category || !price) {
      showDialog(true, "error", "Please fill all required fields");
      return;
    }

    const newItem = {
      label: category,
      label2: subCategory,
      category_service_prize: parseFloat(price),
    };

    setClientServiceForm((prev) => ({
      ...prev,
      serviceItems: [...prev.serviceItems, newItem],
      category: "",
      subCategory: "",
      price: "",
    }));
  };

  const handleAddServiceItemInEdit = () => {
    const { category, subCategory, price } = clientServiceForm;
    if (!category || !price) {
      showDialog(true, "error", "Please fill all required fields");
      return;
    }

    const newItem = {
      label: category,
      label2: subCategory,
      category_service_prize: parseFloat(price),
      _id: `temp-${Date.now()}`, // Temporary ID for new items
    };

    setClientServiceForm((prev) => ({
      ...prev,
      serviceItems: [...prev.serviceItems, newItem],
      category: "",
      subCategory: "",
      price: "",
    }));
  };

  const removeServiceItem = (index) => {
    setClientServiceForm((prev) => ({
      ...prev,
      serviceItems: prev.serviceItems.filter((_, i) => i !== index),
    }));
  };

  const handleClientServiceSubmit = () => {
    if (!canAddClientService) {
      showDialog(
        true,
        "error",
        "You don't have permission to add client services"
      );
      return;
    }

    const { selectedPrimaryService, serviceItems } = clientServiceForm;
    if (!selectedPrimaryService || serviceItems.length === 0) {
      showDialog(
        true,
        "error",
        "Please select a primary service and add at least one service item"
      );
      return;
    }

    const payload = {
      primary_services: selectedPrimaryService,
      name: serviceItems.map((item) => ({
        label: item.label,
        label2: item.label2 || "",
        category_service_prize: parseFloat(item.category_service_prize) || 0,
      })),
    };

    new AdminApi()
      .addServiceCategory(payload)
      .then((res) => {
        showDialog(true, "success", "Client service added successfully");
        getAllServicesExists();
        getAllClientServices();
        setAddClientServiceModalOpen(false);
        setClientServiceForm({
          selectedPrimaryService: "",
          category: "",
          subCategory: "",
          price: "",
          serviceItems: [],
        });
      })
      .catch((err) => {
        console.log(err);
        showDialog(true, "error", err?.response?.data?.message);
      });
  };

  // Show access denied message if user has no permissions for services
  if (!canViewServices && !canViewClientServices) {
    return (
      <Box className="min-h-screen bg-gray-50 p-4 md:p-6 flex items-center justify-center">
        <StyledCard>
          <CardContent>
            <Box className="flex flex-col items-center p-6 text-center">
              <Avatar className="bg-red-100 text-red-600 mb-4 w-16 h-16">
                <CloseIcon className="text-2xl" />
              </Avatar>
              <Typography variant="h6" className="text-gray-800 mb-2">
                Access Denied
              </Typography>
              <Typography variant="body1" className="text-gray-600">
                You don't have permission to view services management. Please
                contact the administrator.
              </Typography>
            </Box>
          </CardContent>
        </StyledCard>
      </Box>
    );
  }

  return (
    <Box className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Snackbar for notifications */}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={snackBarValues.open}
        autoHideDuration={snackBarValues.duration}
        onClose={closeDialog}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={closeDialog}
          severity={snackBarValues.type}
          sx={{ width: "100%", borderRadius: "8px" }}
          variant="filled"
        >
          {snackBarValues.msg}
        </Alert>
      </Snackbar>

      <Box className="mb-6 md:mb-8">
        <div className="flex items-center mb-2">
          <DashboardIcon className="text-indigo-600 mr-3" fontSize="large" />
          <Typography
            variant="h4"
            component="h1"
            fontWeight="bold"
            className="text-gray-800"
          >
            Admin Dashboard
          </Typography>
        </div>
        <Typography variant="body1" className="text-gray-600">
          Manage services and client offerings
        </Typography>
      </Box>

      {/* Action Buttons */}
      <Box className="flex flex-wrap gap-3 mb-6 items-center">
        {canAddPrimaryService && (
          <AddServiceModal
            getAllServicesExists={getAllServicesExists}
            getAllActivePrimaryServices={getAllActivePrimaryServices}
            showDialog={showDialog}
          />
        )}
        {canAddClientService && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={() => setAddClientServiceModalOpen(true)}
            className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-700 hover:to-purple-600"
          >
            Add Client Service
          </Button>
        )}
      </Box>

      {/* Add Client Service Modal */}
      {canAddClientService && (
        <Modal
          open={addClientServiceModalOpen}
          onClose={() => setAddClientServiceModalOpen(false)}
          aria-labelledby="add-client-service-modal"
          closeAfterTransition
        >
          <Fade in={addClientServiceModalOpen}>
            <Box sx={modalStyle} className="bg-white">
              <Box className="flex justify-between items-center mb-4">
                <Typography
                  variant="h6"
                  component="h2"
                  fontWeight="bold"
                  className="text-gray-800"
                >
                  Add Client Service
                </Typography>
                <IconButton
                  onClick={() => setAddClientServiceModalOpen(false)}
                  size="small"
                  className="hover:bg-gray-100"
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6} sx={{ minWidth: 225 }}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel className="text-gray-600">
                      Primary Service
                    </InputLabel>
                    <Select
                      name="selectedPrimaryService"
                      value={clientServiceForm.selectedPrimaryService}
                      onChange={handleClientServiceChange}
                      label="Primary Service"
                      className="rounded-lg"
                    >
                      {primaryServices.map((service) => (
                        <MenuItem
                          key={service._id}
                          value={service._id}
                          className="py-2"
                        >
                          {service.service_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Price"
                    type="number"
                    name="price"
                    value={clientServiceForm.price}
                    onChange={handleClientServiceChange}
                    required
                    className="rounded-lg"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          className="text-gray-500"
                        >
                          ₹
                        </InputAdornment>
                      ),
                      className: "rounded-lg",
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Category"
                    name="category"
                    value={clientServiceForm.category}
                    onChange={handleClientServiceChange}
                    required
                    className="rounded-lg"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Sub Category"
                    name="subCategory"
                    value={clientServiceForm.subCategory}
                    onChange={handleClientServiceChange}
                    className="rounded-lg"
                  />
                </Grid>
              </Grid>

              <Box className="mt-4 flex justify-end">
                <ActionButton
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddServiceItem}
                  className="bg-indigo-600 hover:bg-indigo-700 rounded-lg"
                >
                  Add Service Item
                </ActionButton>
              </Box>

              {clientServiceForm.serviceItems.length > 0 && (
                <Box className="mt-6">
                  <Typography
                    variant="subtitle1"
                    fontWeight="medium"
                    className="text-gray-700 mb-3"
                  >
                    Service Items
                  </Typography>
                  <Grid container spacing={2}>
                    {clientServiceForm.serviceItems.map((item, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Grow in={true} timeout={index * 100}>
                          <AnimatedCard
                            variant="outlined"
                            className="rounded-lg overflow-hidden border-gray-200"
                          >
                            <CardContent className="p-4">
                              <Box className="flex justify-between items-start">
                                <Box>
                                  <Typography
                                    variant="body1"
                                    fontWeight="medium"
                                    className="text-gray-800"
                                  >
                                    {item.label}
                                  </Typography>
                                  {item.label2 && (
                                    <Typography
                                      variant="body2"
                                      className="text-gray-600 mt-1"
                                    >
                                      {item.label2}
                                    </Typography>
                                  )}
                                  <Typography
                                    variant="body2"
                                    className="text-indigo-600 font-semibold mt-1"
                                  >
                                    ₹{item.category_service_prize}
                                  </Typography>
                                </Box>
                                <IconButton
                                  onClick={() => removeServiceItem(index)}
                                  color="error"
                                  size="small"
                                  className="hover:bg-red-50"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </CardContent>
                          </AnimatedCard>
                        </Grow>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              <Box className="flex justify-end gap-3 mt-8">
                <ActionButton
                  variant="outlined"
                  color="inherit"
                  onClick={() => setAddClientServiceModalOpen(false)}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg"
                >
                  Cancel
                </ActionButton>
                <ActionButton
                  variant="contained"
                  color="primary"
                  onClick={handleClientServiceSubmit}
                  disabled={clientServiceForm.serviceItems.length === 0}
                  startIcon={<CheckIcon />}
                  className="bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:bg-gray-300 disabled:text-gray-500"
                >
                  Save Service
                </ActionButton>
              </Box>
            </Box>
          </Fade>
        </Modal>
      )}

      {/* Edit Client Service Modal */}
      {canUpdateClientService && (
        <Modal
          open={editClientServiceModalOpen}
          onClose={() => setEditClientServiceModalOpen(false)}
          aria-labelledby="edit-client-service-modal"
          closeAfterTransition
        >
          <Fade in={editClientServiceModalOpen}>
            <Box sx={modalStyle} className="bg-white">
              <Box className="flex justify-between items-center mb-4">
                <Typography
                  variant="h6"
                  component="h2"
                  fontWeight="bold"
                  className="text-gray-800"
                >
                  Edit Client Service
                </Typography>
                <IconButton
                  onClick={() => setEditClientServiceModalOpen(false)}
                  size="small"
                  className="hover:bg-gray-100"
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel className="text-gray-600">
                      Primary Service
                    </InputLabel>
                    <Select
                      name="selectedPrimaryService"
                      value={clientServiceForm.selectedPrimaryService}
                      onChange={handleClientServiceChange}
                      label="Primary Service"
                      className="rounded-lg"
                    >
                      {primaryServices.map((service) => (
                        <MenuItem
                          key={service._id}
                          value={service._id}
                          className="py-2"
                        >
                          {service.service_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* New Service Item Form */}
              <AnimatedCard
                variant="outlined"
                className="mt-6 mb-4 rounded-lg border-gray-200"
              >
                <CardHeader
                  title="Add New Service Item"
                  titleTypographyProps={{
                    variant: "subtitle1",
                    fontWeight: "medium",
                    className: "text-gray-800",
                  }}
                  className="pb-2"
                />
                <Divider className="bg-gray-200" />
                <CardContent className="pt-4">
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        margin="normal"
                        label="Category *"
                        name="category"
                        value={clientServiceForm.category}
                        onChange={handleClientServiceChange}
                        required
                        className="rounded-lg"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        margin="normal"
                        label="Sub Category"
                        name="subCategory"
                        value={clientServiceForm.subCategory}
                        onChange={handleClientServiceChange}
                        className="rounded-lg"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        margin="normal"
                        label="Price *"
                        type="number"
                        name="price"
                        value={clientServiceForm.price}
                        onChange={handleClientServiceChange}
                        required
                        className="rounded-lg"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment
                              position="start"
                              className="text-gray-500"
                            >
                              ₹
                            </InputAdornment>
                          ),
                          className: "rounded-lg",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box className="flex items-end h-full pb-1">
                        <ActionButton
                          variant="contained"
                          color="primary"
                          startIcon={<AddIcon />}
                          onClick={handleAddServiceItemInEdit}
                          fullWidth
                          className="bg-indigo-600 hover:bg-indigo-700 rounded-lg h-12"
                        >
                          Add Item
                        </ActionButton>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </AnimatedCard>

              {/* Existing Service Items List */}
              {clientServiceForm.serviceItems.length > 0 && (
                <Box className="mt-6">
                  <Typography
                    variant="subtitle1"
                    fontWeight="medium"
                    className="text-gray-700 mb-3"
                  >
                    Current Service Items
                  </Typography>
                  <Grid container spacing={2}>
                    {clientServiceForm.serviceItems.map((item, index) => (
                      <Grid item xs={12} sm={6} key={item._id || index}>
                        <Grow in={true} timeout={index * 100}>
                          <AnimatedCard
                            variant="outlined"
                            className="rounded-lg overflow-hidden border-gray-200"
                          >
                            <CardContent className="p-4">
                              <Box className="flex justify-between items-start">
                                <Box>
                                  <Typography
                                    variant="body1"
                                    fontWeight="medium"
                                    className="text-gray-800"
                                  >
                                    {item.label}
                                  </Typography>
                                  {item.label2 && (
                                    <Typography
                                      variant="body2"
                                      className="text-gray-600 mt-1"
                                    >
                                      {item.label2}
                                    </Typography>
                                  )}
                                  <Typography
                                    variant="body2"
                                    className="text-indigo-600 font-semibold mt-1"
                                  >
                                    ₹{item.category_service_prize}
                                  </Typography>
                                </Box>
                                <IconButton
                                  onClick={() => removeServiceItem(index)}
                                  color="error"
                                  size="small"
                                  className="hover:bg-red-50"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </CardContent>
                          </AnimatedCard>
                        </Grow>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              <Box className="flex justify-end gap-3 mt-8">
                <ActionButton
                  variant="outlined"
                  color="inherit"
                  onClick={() => setEditClientServiceModalOpen(false)}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg"
                >
                  Cancel
                </ActionButton>
                <ActionButton
                  variant="contained"
                  color="primary"
                  onClick={handleUpdateClientService}
                  disabled={clientServiceForm.serviceItems.length === 0}
                  startIcon={<CheckIcon />}
                  className="bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:bg-gray-300 disabled:text-gray-500"
                >
                  Update Service
                </ActionButton>
              </Box>
            </Box>
          </Fade>
        </Modal>
      )}

      {/* Services List with Tabs */}
      <StyledCard className="overflow-hidden">
        <CardHeader
          title="Services Management"
          titleTypographyProps={{
            variant: "h6",
            fontWeight: "bold",
            className: "text-gray-800",
          }}
          action={
            <Typography variant="body2" className="text-gray-500">
              {tabValue === 0
                ? `${servicesList.length} primary services`
                : `${clientServices.length} client services`}
            </Typography>
          }
          className="pb-2"
        />
        <Divider className="bg-gray-200" />
        <CardContent className="pt-2">
          <StyledTabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="services tabs"
            className="mb-4"
          >
            {canViewServices && (
              <Tab
                label="Primary Services"
                icon={<CategoryIcon />}
                iconPosition="start"
              />
            )}
            {canViewClientServices && (
              <Tab
                label="Client Services"
                icon={<DashboardIcon />}
                iconPosition="start"
              />
            )}
          </StyledTabs>

          {canViewServices && (
            <TabPanel value={tabValue} index={0}>
              <ServicesList
                servicesList={servicesList}
                onDeleteService={onDeleteService}
                getAllServicesExists={getAllServicesExists}
                userPermissions={userPermissions}
              />
            </TabPanel>
          )}

          {canViewClientServices && (
            <TabPanel value={tabValue} index={1}>
              {loadingClientServices ? (
                <Box className="flex justify-center p-6">
                  <CircularProgress className="text-indigo-600" />
                </Box>
              ) : clientServices.length > 0 ? (
                <StyledTableContainer className="rounded-lg overflow-hidden">
                  <Table aria-label="client services table">
                    <TableHead>
                      <TableRow>
                        <TableCell className="font-semibold text-gray-700">
                          Primary Service
                        </TableCell>
                        <TableCell className="font-semibold text-gray-700">
                          Category
                        </TableCell>
                        <TableCell className="font-semibold text-gray-700">
                          Sub-Category
                        </TableCell>
                        <TableCell className="font-semibold text-gray-700">
                          Price
                        </TableCell>
                        {(canUpdateClientService || canDeleteClientService) && (
                          <TableCell
                            align="right"
                            className="font-semibold text-gray-700"
                          >
                            Actions
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clientServices.map((service, index) => (
                        <Zoom in={true} timeout={index * 100} key={service.id}>
                          <TableRow
                            hover
                            className="group hover:bg-gray-50 transition-colors duration-150"
                          >
                            <TableCell>
                              <Typography
                                variant="body2"
                                className="text-gray-800"
                              >
                                {service.primaryServiceName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                className="text-gray-800"
                              >
                                {service.category}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                className="text-gray-600"
                              >
                                {service.subCategory || "-"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={`₹${
                                  service.price?.toFixed(2) || "0.00"
                                }`}
                                color="primary"
                                size="small"
                                variant="outlined"
                                className="border-indigo-200 text-indigo-700 bg-indigo-50 font-medium"
                              />
                            </TableCell>
                            {(canUpdateClientService ||
                              canDeleteClientService) && (
                              <TableCell align="right">
                                {canUpdateClientService && (
                                  <Tooltip title="Edit" arrow>
                                    <IconButton
                                      onClick={() =>
                                        onEditClientService(service)
                                      }
                                      color="primary"
                                      size="small"
                                      className="mr-1 hover:bg-indigo-50 transition-colors duration-150"
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {canDeleteClientService && (
                                  <Tooltip title="Delete" arrow>
                                    <IconButton
                                      onClick={() =>
                                        onDeleteClientService(service.id)
                                      }
                                      color="error"
                                      size="small"
                                      className="hover:bg-red-50 transition-colors duration-150"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        </Zoom>
                      ))}
                    </TableBody>
                  </Table>
                </StyledTableContainer>
              ) : (
                <Box className="p-6 bg-gray-50 rounded-lg text-center">
                  <Typography variant="body1" className="text-gray-500">
                    No client services found
                  </Typography>
                </Box>
              )}
            </TabPanel>
          )}
        </CardContent>
      </StyledCard>

      {/* Ticket Acceptance Manager */}
      {canViewTicketAcceptance && (
        <Box className="mt-6">
          <TicketAcceptanceManager userPermissions={userPermissions} />
        </Box>
      )}
    </Box>
  );
}
